/**
 * Super-admin console — three panels in one page:
 *   1. Institution edit (display name, default chapter slug, brand JSON)
 *   2. Chapters CRUD (create / rename / delete)
 *   3. Promote a user to institution-admin by email
 *
 * Gated behind /admin/super on the legacy super-admin route tree; access
 * is enforced server-side via requireSuperAdmin + scoped middleware on
 * the institution PUT.
 */

import { useCallback, useEffect, useState, type FormEvent } from 'react';
import {
  ApiClientError,
  adminUsersApi,
  chaptersApi,
  institutionsApi,
  type ChapterSummary,
  type InstitutionRow,
} from '@/services/api';

export function SuperAdminPage() {
  return (
    <div className="space-y-10">
      <header>
        <h1 className="text-3xl font-display font-bold text-white">Super admin</h1>
        <p className="text-white/60 text-sm mt-1">Institution, chapters, and user promotion.</p>
      </header>
      <InstitutionPanel />
      <ChaptersPanel />
      <PromotePanel />
    </div>
  );
}

function InstitutionPanel() {
  const [inst, setInst] = useState<InstitutionRow | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [savedAt, setSavedAt] = useState<Date | null>(null);
  const [displayName, setDisplayName] = useState('');
  const [defaultChapterSlug, setDefaultChapterSlug] = useState('');
  const [brand, setBrand] = useState('');
  const [saving, setSaving] = useState(false);

  const reload = useCallback(async () => {
    try {
      const row = await institutionsApi.get('navarasam');
      setInst(row);
      setDisplayName(row.displayName);
      setDefaultChapterSlug(row.defaultChapterSlug ?? '');
      setBrand(row.brandSettingsJson ?? '');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load institution');
    }
  }, []);

  useEffect(() => {
    void reload();
  }, [reload]);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    if (!inst) return;
    setSaving(true);
    setError(null);
    try {
      const next = await institutionsApi.update(inst.id, {
        displayName,
        defaultChapterSlug: defaultChapterSlug.trim() || null,
        brandSettingsJson: brand.trim() || null,
      });
      setInst(next);
      setSavedAt(new Date());
    } catch (err) {
      if (err instanceof ApiClientError) setError(err.message);
      else setError(err instanceof Error ? err.message : 'Update failed');
    } finally {
      setSaving(false);
    }
  }

  if (!inst) {
    return (
      <section className="card">
        <h2 className="text-xl font-semibold text-white mb-2">Institution</h2>
        {error ? <p className="text-red-400">{error}</p> : <p className="text-white/60">Loading…</p>}
      </section>
    );
  }

  return (
    <section className="card">
      <h2 className="text-xl font-semibold text-white mb-3">Institution — {inst.slug}</h2>
      <form className="grid md:grid-cols-2 gap-4" onSubmit={onSubmit}>
        <div>
          <label className="block text-sm text-white/80 mb-1">Display name</label>
          <input className="input" value={displayName} onChange={(e) => setDisplayName(e.target.value)} />
        </div>
        <div>
          <label className="block text-sm text-white/80 mb-1">Default chapter slug</label>
          <input className="input" value={defaultChapterSlug} onChange={(e) => setDefaultChapterSlug(e.target.value)} placeholder="96" />
        </div>
        <div className="md:col-span-2">
          <label className="block text-sm text-white/80 mb-1">Brand settings (JSON)</label>
          <textarea
            className="input font-mono text-xs"
            rows={4}
            value={brand}
            onChange={(e) => setBrand(e.target.value)}
            placeholder='{"primaryColor":"#e94560"}'
          />
        </div>
        <div className="md:col-span-2 flex items-center gap-3">
          <button type="submit" className="btn-primary" disabled={saving}>
            {saving ? 'Saving…' : 'Save'}
          </button>
          {savedAt && <span className="text-xs text-green-400">Saved {savedAt.toLocaleTimeString()}</span>}
          {error && <span className="text-xs text-red-400">{error}</span>}
        </div>
      </form>
    </section>
  );
}

function ChaptersPanel() {
  const [rows, setRows] = useState<ChapterSummary[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [newSlug, setNewSlug] = useState('');
  const [newName, setNewName] = useState('');
  const [newDisplay, setNewDisplay] = useState('');
  const [newYear, setNewYear] = useState('');
  const [newLocation, setNewLocation] = useState('');
  const [editing, setEditing] = useState<ChapterSummary | null>(null);

  const reload = useCallback(async () => {
    try {
      const cs = await chaptersApi.list('navarasam');
      setRows(cs);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load chapters');
    }
  }, []);

  useEffect(() => {
    void reload();
  }, [reload]);

  async function handleCreate(e: FormEvent) {
    e.preventDefault();
    if (!newSlug.trim() || !newName.trim()) return;
    setBusy(true);
    setError(null);
    try {
      await chaptersApi.create({
        slug: newSlug.trim(),
        name: newName.trim(),
        displayName: newDisplay.trim() || newName.trim(),
        batchYear: newYear ? Number(newYear) : undefined,
        location: newLocation.trim() || undefined,
      });
      setNewSlug(''); setNewName(''); setNewDisplay(''); setNewYear(''); setNewLocation('');
      await reload();
    } catch (err) {
      if (err instanceof ApiClientError) setError(err.message);
      else setError(err instanceof Error ? err.message : 'Create failed');
    } finally {
      setBusy(false);
    }
  }

  async function handleDelete(id: string, name: string) {
    if (!window.confirm(`Delete chapter "${name}"? This cannot be undone.`)) return;
    try {
      await chaptersApi.remove(id);
      await reload();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Delete failed');
    }
  }

  return (
    <section className="card">
      <h2 className="text-xl font-semibold text-white mb-3">Chapters</h2>
      {error && <p className="text-red-400 text-sm mb-2">{error}</p>}

      <form className="grid md:grid-cols-6 gap-3 items-end mb-4" onSubmit={handleCreate}>
        <div className="md:col-span-1">
          <label className="block text-xs text-white/60 mb-1">Slug</label>
          <input className="input" value={newSlug} onChange={(e) => setNewSlug(e.target.value)} placeholder="97" required />
        </div>
        <div className="md:col-span-2">
          <label className="block text-xs text-white/60 mb-1">Name</label>
          <input className="input" value={newName} onChange={(e) => setNewName(e.target.value)} placeholder="Class of 1997" required />
        </div>
        <div className="md:col-span-1">
          <label className="block text-xs text-white/60 mb-1">Year</label>
          <input className="input" type="number" value={newYear} onChange={(e) => setNewYear(e.target.value)} placeholder="1997" />
        </div>
        <div className="md:col-span-1">
          <label className="block text-xs text-white/60 mb-1">Location</label>
          <input className="input" value={newLocation} onChange={(e) => setNewLocation(e.target.value)} />
        </div>
        <div className="md:col-span-1">
          <button type="submit" className="btn-primary w-full" disabled={busy}>
            {busy ? 'Creating…' : 'Create'}
          </button>
        </div>
      </form>

      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="text-left text-white/60 uppercase text-xs">
            <tr>
              <th className="px-2 py-2">Slug</th>
              <th className="px-2 py-2">Display name</th>
              <th className="px-2 py-2">Year</th>
              <th className="px-2 py-2">Location</th>
              <th className="px-2 py-2"></th>
            </tr>
          </thead>
          <tbody className="text-white/80">
            {rows.map((c) => (
              <tr key={c.id} className="border-t border-white/10">
                <td className="px-2 py-2 font-mono">{c.slug}</td>
                <td className="px-2 py-2">{c.displayName}</td>
                <td className="px-2 py-2">{c.batchYear ?? '—'}</td>
                <td className="px-2 py-2">{c.location ?? '—'}</td>
                <td className="px-2 py-2 text-right">
                  <div className="flex gap-2 justify-end">
                    <button onClick={() => setEditing(c)} className="text-xs px-2 py-1 bg-white/10 rounded hover:bg-white/20">
                      Edit
                    </button>
                    <button onClick={() => handleDelete(c.id, c.displayName)} className="text-xs px-2 py-1 bg-red-500/20 text-red-300 rounded hover:bg-red-500/30">
                      Delete
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {editing && (
        <EditChapterModal
          chapter={editing}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); void reload(); }}
        />
      )}
    </section>
  );
}

function EditChapterModal({
  chapter,
  onClose,
  onSaved,
}: {
  chapter: ChapterSummary;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [name, setName] = useState(chapter.name);
  const [displayName, setDisplayName] = useState(chapter.displayName);
  const [batchYear, setBatchYear] = useState<string>(chapter.batchYear?.toString() ?? '');
  const [location, setLocation] = useState(chapter.location ?? '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleSave(e: FormEvent) {
    e.preventDefault();
    setSaving(true);
    setError(null);
    try {
      await chaptersApi.update(chapter.id, {
        name: name.trim(),
        displayName: displayName.trim(),
        batchYear: batchYear ? Number(batchYear) : undefined,
        location: location.trim() || undefined,
      });
      onSaved();
    } catch (err) {
      if (err instanceof ApiClientError) setError(err.message);
      else setError(err instanceof Error ? err.message : 'Save failed');
    } finally {
      setSaving(false);
    }
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
    >
      <form
        className="card max-w-xl w-full space-y-3"
        onClick={(e) => e.stopPropagation()}
        onSubmit={handleSave}
      >
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold text-white">Edit chapter — {chapter.slug}</h2>
          <button
            type="button"
            onClick={onClose}
            className="text-white/60 hover:text-white"
            aria-label="Close"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        <div className="grid md:grid-cols-2 gap-3">
          <div>
            <label className="block text-xs text-white/60 mb-1">Slug (read-only)</label>
            <input className="input bg-white/5 text-white/60" value={chapter.slug} readOnly />
          </div>
          <div>
            <label className="block text-xs text-white/60 mb-1">Year</label>
            <input
              className="input"
              type="number"
              value={batchYear}
              min={1900}
              max={2200}
              onChange={(e) => setBatchYear(e.target.value)}
            />
          </div>
          <div className="md:col-span-2">
            <label className="block text-xs text-white/60 mb-1">Name</label>
            <input className="input" value={name} maxLength={255} onChange={(e) => setName(e.target.value)} />
          </div>
          <div className="md:col-span-2">
            <label className="block text-xs text-white/60 mb-1">Display name</label>
            <input className="input" value={displayName} maxLength={255} onChange={(e) => setDisplayName(e.target.value)} />
          </div>
          <div className="md:col-span-2">
            <label className="block text-xs text-white/60 mb-1">Location</label>
            <input className="input" value={location} maxLength={255} onChange={(e) => setLocation(e.target.value)} />
          </div>
        </div>
        {error && <p className="text-sm text-red-400">{error}</p>}
        <div className="flex justify-end gap-2 pt-1">
          <button type="button" onClick={onClose} className="btn-secondary">Cancel</button>
          <button type="submit" className="btn-primary" disabled={saving}>
            {saving ? 'Saving…' : 'Save'}
          </button>
        </div>
      </form>
    </div>
  );
}

function PromotePanel() {
  const [userId, setUserId] = useState('');
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  async function handlePromote(e: FormEvent) {
    e.preventDefault();
    if (!userId.trim()) return;
    setBusy(true);
    setError(null);
    setResult(null);
    try {
      const res = await adminUsersApi.promote(userId.trim(), 'navarasam');
      setResult(res.created ? 'Promoted (membership created).' : res.updated ? 'Promoted (membership updated).' : 'Done.');
    } catch (err) {
      if (err instanceof ApiClientError) setError(err.message);
      else setError(err instanceof Error ? err.message : 'Promote failed');
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="card">
      <h2 className="text-xl font-semibold text-white mb-3">Promote user to institution admin</h2>
      <p className="text-white/60 text-sm mb-3">
        Grants a Membership at institution scope with role=admin. The user inherits admin on every
        chapter and branch in the institution. Look up the user id on the Users page first.
      </p>
      <form className="flex gap-3 items-end" onSubmit={handlePromote}>
        <div className="flex-1">
          <label className="block text-xs text-white/60 mb-1">User id (UUID)</label>
          <input className="input font-mono" value={userId} onChange={(e) => setUserId(e.target.value)} required />
        </div>
        <button type="submit" className="btn-primary" disabled={busy}>
          {busy ? 'Promoting…' : 'Promote'}
        </button>
      </form>
      {result && <p className="text-green-400 text-sm mt-3">{result}</p>}
      {error && <p className="text-red-400 text-sm mt-3">{error}</p>}
    </section>
  );
}

export default SuperAdminPage;
